'use client';
import { resolveDisplayClass } from '@/components/proglove/display/displayClass';
import { renderMai } from '@/components/proglove/display/renderMai';
import { renderMarkDisplay } from '@/components/proglove/display/renderMarkDisplay';
import {
  type GloveCommandBody,
  isTransientScreen,
  maiScreenOf,
  type ProGloveScreen,
} from '@/components/proglove/display/screens';
import useCommandPacer, { type CommandBudget } from '@/components/proglove/useCommandPacer';
import { useWebSocket } from '@/components/websocket/WebSocketProvider';
import createContextHardened from '@/helper/createContextHardened';
import { delayedSync } from '@/helper/delay';
import { ESP_DEBUG } from '@/helper/errors';
import { useInputNavigation } from '@/helper/useInputNavigation';
import useStableCallback from '@/helper/useStableCallback';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type ProGloveDeviceSerial = string;

export interface ProGloveEvent {
  api_version: string;
  event_type: string;
  event_id: string;
  time_created: number;
  disconnect?: 'all' | string[];
  connect?: boolean;
}

export interface ProGloveEventScan extends ProGloveEvent {
  event_type: 'scan';
  scan_code: string;
  scan_bytes: string;
  device_serial: ProGloveDeviceSerial;
  device_model: string;
  gateway_serial: string;
}

export interface ProGloveEventButtonPress extends ProGloveEvent {
  event_type: 'button_pressed';
  trigger_gesture: ProGloveTriggerGesture;
  device_serial: ProGloveDeviceSerial;
  gateway_serial: string;
  /** set when an assigned physical action button (MAI) was pressed: the action id */
  ref_id?: string;
  button_id?: number;
}

export interface ProGloveEventScannerState extends ProGloveEvent {
  event_type: 'scanner_state';
  device_connected_state: 'STATE_DISCONNECTED' | string;
  device_disconnect_reason: 'UNKNOWN' | string;
  device_serial: ProGloveDeviceSerial;
}

// A paired device, built from the "already paired" connect rejection and from
// scan/scanner_state events (see README.md).
export interface ProGloveDevice {
  device_serial: ProGloveDeviceSerial;
  device_model?: string;
}

// The only response the Streams API sends for a failed command; there are no
// success acknowledgements (see README.md).
// https://docs.proglove.com/en/error-event-48591.html
export interface ProGloveEventError extends ProGloveEvent {
  event_type: 'errors';
  event_reference_id?: string;
  error_severity?: 'WARNING' | 'ERROR' | 'CRITICAL';
  error_message?: string;
  error_code?: 'ERROR_UNKNOWN' | 'ERROR_INVALID_COMMAND' | 'ERROR_TIMEOUT' | 'ERROR_DEVICE_NOT_FOUND';
  device_serial?: ProGloveDeviceSerial;
}

export interface ProGloveCommand {
  api_version: string;
  event_type: string;
  event_id: string;
  time_created: number;
}

export enum ProGloveWorkerFeedbackActionId {
  FEEDBACK_POSITIVE = 'FEEDBACK_POSITIVE',
  FEEDBACK_NEGATIVE = 'FEEDBACK_NEGATIVE',
  FEEDBACK_SPECIAL_YELLOW = 'FEEDBACK_SPECIAL_YELLOW',
  FEEDBACK_SPECIAL_PURPLE = 'FEEDBACK_SPECIAL_PURPLE',
  FEEDBACK_SPECIAL_CYAN = 'FEEDBACK_SPECIAL_CYAN',
}

/**
 * The feedback that goes with a result's outcome: a warning (e.g. nothing found)
 * is no failure, so it gets the neutral yellow LED instead of the negative one.
 */
/** how a flow ended; a warning (e.g. nothing found) is no failure */
export type ProGloveOutcome = 'success' | 'warning' | 'failure';

export const resultFeedbackAction: Record<ProGloveOutcome, ProGloveWorkerFeedbackActionId> = {
  success: ProGloveWorkerFeedbackActionId.FEEDBACK_POSITIVE,
  warning: ProGloveWorkerFeedbackActionId.FEEDBACK_SPECIAL_YELLOW,
  failure: ProGloveWorkerFeedbackActionId.FEEDBACK_NEGATIVE,
};

export enum ProGloveTriggerGesture {
  TRIGGER_SINGLE_CLICK = 'TRIGGER_SINGLE_CLICK',
  TRIGGER_DOUBLE_CLICK = 'TRIGGER_DOUBLE_CLICK',
  TRIGGER_TRIPLE_CLICK = 'TRIGGER_TRIPLE_CLICK',
}

export interface ProGloveCommandWorkerFeedback extends ProGloveCommand {
  device_serial: ProGloveDeviceSerial;
  feedback_action_id: ProGloveWorkerFeedbackActionId;
}

export interface ProGloveCommandButtonBlocking extends ProGloveCommand {
  device_serial: ProGloveDeviceSerial;
  time_validity_duration?: number;
  trigger_block_state?: boolean;
}

export const isProGloveEventScan = (event: ProGloveEvent): event is ProGloveEventScan => {
  return event.event_type === 'scan';
};

export const isProGloveEventButtonPress = (event: ProGloveEvent): event is ProGloveEventButtonPress => {
  return event.event_type === 'button_pressed';
};

export const isProGloveEventScannerState = (event: ProGloveEvent): event is ProGloveEventScannerState => {
  return event.event_type === 'scanner_state';
};

/**
 * A MAI screen interaction (device-verified): a tap on an on-screen component,
 * e.g. a list item, reports that component's ref_id. Physical buttons arrive as
 * button_pressed instead.
 */
export interface ProGloveEventDisplayV2 extends ProGloveEvent {
  event_type: 'display_v2';
  device_serial?: ProGloveDeviceSerial;
  screen_event?: {
    component_clicked?: { ref_id: string };
  };
}

export const isProGloveEventDisplayV2 = (event: ProGloveEvent): event is ProGloveEventDisplayV2 => {
  return event.event_type === 'display_v2';
};

export const isProGloveEventError = (event: ProGloveEvent): event is ProGloveEventError => {
  return event.event_type === 'errors';
};

export type ScanHandler = (scanCode: string) => null | React.MutableRefObject<HTMLFormElement | null>;
export type ButtonPressHandler = (triggerGesture: ProGloveTriggerGesture) => void;
/** receives the `id` of the pressed screen action (`GloveAction`) */
export type GloveActionHandler = (actionId: string) => void;

/** Connection ladder: websocket to Insight Mobile first, then a paired scanner on top. */
export enum ProGlovePhase {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  NO_SCANNER = 'no-scanner',
  READY = 'ready',
}

export interface ProGloveLastScan {
  scanCode: string;
  deviceSerial: ProGloveDeviceSerial;
  time: number;
}

export interface ProGloveErrorLogEntry {
  eventId: string;
  time: number;
  severity: 'WARNING' | 'ERROR' | 'CRITICAL';
  code: string | null;
  message: string | null;
  deviceSerial: ProGloveDeviceSerial | null;
}

// display payloads need non-ASCII \uXXXX-escaped, or the device answers
// "No suitable font" (see README.md).
const jsonToAscii = (json: string): string =>
  json.replace(/[\u0080-\uffff]/g, (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`);

const MAX_ERROR_LOG_ENTRIES = 10;

const ERROR_LOG_STORAGE_KEY = 'EKKO_PROGLOVE_ERROR_LOG';

// Rebuilds a log entry from untrusted storage data: every field is validated
// or coerced, entries without the essential fields are dropped.
const sanitizeErrorLogEntry = (entry: unknown): ProGloveErrorLogEntry | null => {
  if (typeof entry !== 'object' || entry === null) {
    return null;
  }

  const eventId = 'eventId' in entry ? entry.eventId : null;
  const time = 'time' in entry ? entry.time : null;
  const severity = 'severity' in entry ? entry.severity : null;
  const code = 'code' in entry ? entry.code : null;
  const message = 'message' in entry ? entry.message : null;
  const deviceSerial = 'deviceSerial' in entry ? entry.deviceSerial : null;

  if (typeof eventId !== 'string' || typeof time !== 'number' || !Number.isFinite(time)) {
    return null;
  }

  return {
    eventId,
    time,
    severity: severity === 'WARNING' || severity === 'CRITICAL' || severity === 'ERROR' ? severity : 'ERROR',
    code: typeof code === 'string' ? code : null,
    message: typeof message === 'string' ? message : null,
    deviceSerial: typeof deviceSerial === 'string' ? deviceSerial : null,
  };
};

// The error log survives page reloads so intermittent device problems stay
// visible on the status page's maintenance section until cleared.
const loadPersistedErrorLogs = (): ProGloveErrorLogEntry[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(ERROR_LOG_STORAGE_KEY);
    if (raw === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map(sanitizeErrorLogEntry)
      .filter((entry): entry is ProGloveErrorLogEntry => entry !== null)
      .slice(0, MAX_ERROR_LOG_ENTRIES);
  } catch {
    return [];
  }
};

const persistErrorLogs = (entries: ProGloveErrorLogEntry[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(ERROR_LOG_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // storage may be unavailable; the log is then session-only
  }
};

interface ProGloveContextState {
  phase: ProGlovePhase;
  /** currently paired scanners as derived from scanner_state and scan events (plus localStorage restore) */
  devices: ProGloveDevice[];
  /** serial of the scanner commands are sent to (display!, feedback!, ...) */
  activeDeviceSerial: ProGloveDeviceSerial | null;
  lastScan: ProGloveLastScan | null;
  /** most recent Streams API errors events, newest first; collected app-wide and persisted across reloads */
  recentErrors: ProGloveErrorLogEntry[];
  /** empties the persisted error log */
  clearRecentErrors: () => void;
  /** Asks Insight Mobile to pair a scanner. With `reset`, a paired one is unpaired first. */
  pairScanner: (reset?: boolean) => void;
  /**
   * Shows a screen on the active device, rendered for its display class: at
   * most one command, none for devices without a display (see README.md,
   * "Displays"). Call sites normally use the `useProGloveScreen` hook.
   */
  showScreen: (screen: ProGloveScreen) => void;
  workerFeedback: (feedbackActionId: ProGloveWorkerFeedbackActionId, deviceSerial?: ProGloveDeviceSerial) => void;
  buttonBlocking: (state?: boolean, duration?: number) => void;
  addScanListener: (handler: ScanHandler) => void;
  addButtonPressListener: (handler: ButtonPressHandler) => void;
  removeScanListener: (handler: ScanHandler) => void;
  removeButtonPressListener: (handler: ButtonPressHandler) => void;
  addGloveActionListener: (handler: GloveActionHandler) => void;
  removeGloveActionListener: (handler: GloveActionHandler) => void;
}

const [ProGloveContext, useProGlove] = createContextHardened<ProGloveContextState>('ProGlove');
export { useProGlove };

interface ProGloveProviderProps {
  children: React.ReactNode;
  /** replaces the per-model command budgets (see useCommandPacer), e.g. unlimited in tests */
  commandBudget?: CommandBudget;
}

/**
 * What a glove's command queue in Insight Mobile takes, with a margin below
 * the measured values (2026-10-07: MAI 6 at once and one every ~90 ms, MARK
 * Display 5 at once and one screen every ~700 ms). Devices without a display
 * only get feedback; they use the cautious MARK Display values until measured.
 */
const COMMAND_BUDGETS: Record<'mai' | 'other', CommandBudget> = {
  mai: { burst: 4, refillMs: 120 },
  other: { burst: 3, refillMs: 800 },
};

export function ProGloveProvider({ children, commandBudget }: ProGloveProviderProps): React.JSX.Element {
  const {
    connected: isWsConnected,
    connecting: isWsConnecting,
    connect,
    disconnect,
    send,
    addOnOpenListener,
    removeOnOpenListener,
    addOnMessageListener,
    removeOnMessageListener,
  } = useWebSocket();
  const scanHandlersRef = useRef<Set<ScanHandler>>(new Set());
  const buttonPressHandlersRef = useRef<Set<ButtonPressHandler>>(new Set());
  const gloveActionHandlersRef = useRef<Set<GloveActionHandler>>(new Set());
  const [scannerConnected, setScannerConnected] = useState<boolean>(false);
  const [scannerDeviceSerial, setScannerDeviceSerial] = useState<ProGloveDeviceSerial | null>(null);
  const [devices, setDevices] = useState<ProGloveDevice[]>([]);

  // The device commands go to: the tracked entry, or a bare serial while the
  // device list has not caught up yet.
  const activeDevice =
    scannerDeviceSerial === null
      ? undefined
      : (devices.find((d) => d.device_serial === scannerDeviceSerial) ?? { device_serial: scannerDeviceSerial });
  const displayClass = resolveDisplayClass(activeDevice);

  // device commands (screens, feedback, button blocking) go out within the
  // glove's budget; pairing addresses Insight Mobile itself and is sent directly
  const pacer = useCommandPacer(send, commandBudget ?? COMMAND_BUDGETS[displayClass === 'mai' ? 'mai' : 'other']);
  const [lastScan, setLastScan] = useState<ProGloveLastScan | null>(null);
  const [recentErrors, setRecentErrors] = useState<ProGloveErrorLogEntry[]>([]);
  /** mirror of recentErrors so updates can compute+persist synchronously, free of effect-ordering races */
  const recentErrorsRef = useRef<ProGloveErrorLogEntry[]>([]);

  // Single write path for the error log: state, ref mirror and localStorage
  // always change together, so storage cannot be overwritten with a stale state.
  const updateRecentErrors = useCallback(
    (updater: (prev: ProGloveErrorLogEntry[]) => ProGloveErrorLogEntry[]): void => {
      const next = updater(recentErrorsRef.current).slice(0, MAX_ERROR_LOG_ENTRIES);
      recentErrorsRef.current = next;
      setRecentErrors(next);
      persistErrorLogs(next);
    },
    [],
  );

  // Loaded after mount, not as initial state: localStorage is unavailable
  // during SSR and a differing first render would break hydration. Merges by
  // event id instead of overwriting, so no entry is lost to a concurrent error
  // or a repeated effect run (dev StrictMode).
  useEffect(() => {
    const stored = loadPersistedErrorLogs();
    updateRecentErrors((prev) => [
      ...prev,
      ...stored.filter((entry) => !prev.some((existing) => existing.eventId === entry.eventId)),
    ]);
  }, [updateRecentErrors]);

  const clearRecentErrors = useCallback(() => {
    updateRecentErrors(() => []);
  }, [updateRecentErrors]);
  // event_ids of ALL in-flight connectivity commands, to correlate their
  // `errors` responses. Several can be outstanding at once, so a single
  // "latest id" slot would mis-route the older ones.
  const pendingConnectivityIdsRef = useRef<Set<string>>(new Set());

  /**
   * The last screen body sent to the active device. An identical one is skipped:
   * the glove already shows it (e.g. a screen rebuilt when a scan color expires,
   * or two components showing the same screen). Forgotten whenever the device
   * changes or the socket is lost, so a glove that comes back gets it again.
   */
  const lastSentScreenRef = useRef<string | null>(null);

  // Losing the socket means losing all knowledge about the paired scanner: no
  // events arrive while disconnected, and an unpair in the meantime produces
  // none on reconnect either, so the phase would stay 'ready' with a device the
  // app cannot talk to. A still-paired scanner is restored by the reconnect
  // handshake's "already paired" rejection (see README.md).
  useEffect(() => {
    if (!isWsConnected) {
      setScannerConnected(false);
      setScannerDeviceSerial(null);
      setDevices([]);
      // nothing waiting may go out to whatever device is paired after reconnecting
      pacer.clear();
      lastSentScreenRef.current = null;
    }
  }, [isWsConnected, pacer]);

  const inputNavigation = useInputNavigation();

  const pairScanner = useCallback(
    (reset: boolean = false) => {
      // `disconnect: 'all'` + `connect: true` unpairs first: only for explicit
      // user-triggered re-pairing, never on a socket (re)connect (see README.md).
      const connectivityCommand: ProGloveEvent = {
        api_version: '1.0',
        event_type: 'scanner_connectivity!',
        event_id: uuidv4(),
        time_created: Date.now(),
        ...(reset && { disconnect: 'all' }),
        connect: true,
      };

      pendingConnectivityIdsRef.current.add(connectivityCommand.event_id);

      if (ESP_DEBUG) {
        console.log('proglove websocket send: ', connectivityCommand);
      }
      send(JSON.stringify(connectivityCommand));
    },
    [send],
  );

  const workerFeedback = useCallback(
    (feedbackActionId: ProGloveWorkerFeedbackActionId, deviceSerial?: ProGloveDeviceSerial) => {
      const targetSerial = deviceSerial ?? scannerDeviceSerial;

      if (targetSerial !== null) {
        const feedbackCommand: ProGloveCommandWorkerFeedback = {
          api_version: '1.0',
          event_type: 'feedback!',
          event_id: uuidv4(),
          time_created: Date.now(),
          device_serial: targetSerial,
          feedback_action_id: feedbackActionId,
        };

        pacer.sendCommand(JSON.stringify(feedbackCommand));
      }
    },
    [scannerDeviceSerial, pacer],
  );

  const buttonBlocking = useCallback(
    (blockState: boolean = true, duration: number = 0) => {
      if (scannerDeviceSerial !== null) {
        const triggerBlockCommand: ProGloveCommandButtonBlocking = {
          api_version: '1.0',
          event_type: 'trigger_block!',
          event_id: uuidv4(),
          time_created: Date.now(),
          device_serial: scannerDeviceSerial,
          time_validity_duration: duration,
          trigger_block_state: blockState,
        };

        pacer.sendCommand(JSON.stringify(triggerBlockCommand));
      }
    },
    [scannerDeviceSerial, pacer],
  );

  /** last screen that is not a timed result — what the glove should show once nothing else is pending */
  const baseScreenRef = useRef<ProGloveScreen | null>(null);
  /** MAI has no validity duration, so the app restores the base screen after a timed result itself */
  const maiRestoreTimerRef = useRef<number | null>(null);

  const clearMaiRestoreTimer = useCallback((): void => {
    if (maiRestoreTimerRef.current !== null) {
      window.clearTimeout(maiRestoreTimerRef.current);
      maiRestoreTimerRef.current = null;
    }
  }, []);

  const sendScreen = useStableCallback(
    (screen: ProGloveScreen): void => {
      if (scannerDeviceSerial === null) {
        return;
      }

      let body: GloveCommandBody;
      switch (displayClass) {
        case 'mark-display':
          body = renderMarkDisplay(screen.markDisplay);
          break;
        case 'mai':
          body = renderMai(maiScreenOf(screen));
          break;
        default:
          // no display, or not known yet: nothing to send
          return;
      }

      const bodyKey = JSON.stringify(body);
      if (bodyKey === lastSentScreenRef.current) {
        return;
      }
      lastSentScreenRef.current = bodyKey;

      // envelope first, in the key order display! always had
      const { api_version, event_type, ...rest } = body;
      pacer.sendDisplay(
        jsonToAscii(
          JSON.stringify({
            api_version,
            event_type,
            event_id: uuidv4(),
            time_created: Date.now(),
            device_serial: scannerDeviceSerial,
            ...rest,
          }),
        ),
      );
    },
    [scannerDeviceSerial, displayClass, pacer],
  );

  const showScreen = useStableCallback(
    (screen: ProGloveScreen): void => {
      clearMaiRestoreTimer();

      const transient = isTransientScreen(screen);
      if (!transient) {
        baseScreenRef.current = screen;
      }

      sendScreen(screen);

      const mai = maiScreenOf(screen);
      if (transient && displayClass === 'mai' && mai.kind === 'notification') {
        maiRestoreTimerRef.current = window.setTimeout(() => {
          maiRestoreTimerRef.current = null;
          if (baseScreenRef.current !== null) {
            sendScreen(baseScreenRef.current);
          }
        }, mai.durationMs);
      }
    },
    [clearMaiRestoreTimer, sendScreen, displayClass],
  );

  // A new or newly identified device (pairing, reload rediscovery, a scan
  // delivering the model) gets the screen the app is currently showing.
  useEffect(() => {
    clearMaiRestoreTimer();
    lastSentScreenRef.current = null;
    if (baseScreenRef.current !== null) {
      sendScreen(baseScreenRef.current);
    }
  }, [scannerDeviceSerial, displayClass, clearMaiRestoreTimer, sendScreen]);

  useEffect(() => clearMaiRestoreTimer, [clearMaiRestoreTimer]);

  /** A scan: remember it, learn the device from it, then fan out to the listeners. */
  const onScan = useStableCallback(
    (data: ProGloveEventScan) => {
      setLastScan({ scanCode: data.scan_code, deviceSerial: data.device_serial, time: Date.now() });

      // a scan proves the scanner is paired and talking to us — recover the
      // tracked serial even if no scanner_state event was received
      if (!scannerConnected || scannerDeviceSerial !== data.device_serial) {
        setScannerConnected(true);
        setScannerDeviceSerial(data.device_serial);
      }
      // a scan is the only source of the official device_model — update an
      // entry created without one, or add the device if it is unknown
      setDevices((prev) => {
        const existing = prev.find((d) => d.device_serial === data.device_serial);
        if (existing === undefined) {
          return [...prev, { device_serial: data.device_serial, device_model: data.device_model }];
        }
        return existing.device_model === data.device_model
          ? prev
          : prev.map((d) => (d.device_serial === data.device_serial ? { ...d, device_model: data.device_model } : d));
      });

      if (inputNavigation.execSync(data.scan_code)) {
        return;
      }

      scanHandlersRef.current.forEach((handler) => {
        const refForm = handler(data.scan_code);

        if (refForm !== null) {
          delayedSync(() => {
            refForm.current?.requestSubmit();
          });
        }
      });
    },
    [inputNavigation, scannerConnected, scannerDeviceSerial],
  );

  /**
   * A button press: either a press of a physical action button a screen assigned
   * (MAI, carries the action id as ref_id), or a trigger gesture.
   */
  const onButtonPress = useStableCallback((data: ProGloveEventButtonPress) => {
    const actionId = data.ref_id;
    if (actionId !== undefined && actionId !== '') {
      gloveActionHandlersRef.current.forEach((handler) => {
        handler(actionId);
      });
      return;
    }

    buttonPressHandlersRef.current.forEach((handler) => {
      handler(data.trigger_gesture);
    });
  }, []);

  /** A tap on a screen component: reported like a physical button's action. */
  const onDisplayEvent = useStableCallback((data: ProGloveEventDisplayV2) => {
    const actionId = data.screen_event?.component_clicked?.ref_id;
    if (actionId === undefined || actionId === '') {
      return;
    }

    gloveActionHandlersRef.current.forEach((handler) => {
      handler(actionId);
    });
  }, []);

  /** Pair/unpair of a device — the only event Insight Mobile pushes about connectivity. */
  const onScannerStateChange = useStableCallback(
    (data: ProGloveEventScannerState) => {
      if (data.device_connected_state === 'STATE_DISCONNECTED') {
        setDevices((prev) => prev.filter((d) => d.device_serial !== data.device_serial));

        if (scannerDeviceSerial !== null && scannerDeviceSerial !== data.device_serial) {
          // a different scanner disconnected, not the one we are tracking
          return;
        }

        if (ESP_DEBUG) {
          console.log('proglove: scanner disconnected, sending connectivity command');
        }

        setScannerConnected(false);
        setScannerDeviceSerial(null);
        // Re-arm pairing so a scanner lost spontaneously (out of range, empty
        // battery) offers SCAN2PAIR again. After a user-triggered Disconnect
        // this duplicates the reset command's own connect flag — a harmless
        // no-op, and suppressing it risks eating a legitimate re-arm later.
        pairScanner();

        return;
      }

      if (data.device_connected_state === 'STATE_CONNECTED') {
        setScannerConnected(true);
        setScannerDeviceSerial(data.device_serial);
        setDevices((prev) =>
          prev.some((d) => d.device_serial === data.device_serial)
            ? prev
            : [...prev, { device_serial: data.device_serial }],
        );
      }
    },
    [scannerDeviceSerial, pairScanner],
  );

  /** The only failure response the API sends — and the answer to our pairing probe. */
  const onProGloveError = useStableCallback(
    (data: ProGloveEventError) => {
      // Insight Mobile rejects connect while a scanner is paired, and that
      // rejection carries the paired serial — the only reload-safe way to
      // learn a device is there. Matching the English message is fragile but
      // deliberately kept simple (see README.md).
      if (
        data.event_reference_id !== undefined &&
        pendingConnectivityIdsRef.current.delete(data.event_reference_id) &&
        /already.*paired/i.test(data.error_message ?? '')
      ) {
        if (ESP_DEBUG) {
          console.log('proglove: connect rejected, a device is already paired:', data);
        }

        setScannerConnected(true);

        // No device_model in the rejection: the UI infers it from the serial
        // prefix (see scannerModel.ts) until a scan delivers the real one.
        const rejectionSerial =
          data.device_serial !== undefined && data.device_serial !== '' && !data.device_serial.startsWith('<')
            ? data.device_serial
            : null;

        if (rejectionSerial !== null) {
          setScannerDeviceSerial(rejectionSerial);
          setDevices((prev) =>
            prev.some((d) => d.device_serial === rejectionSerial)
              ? prev
              : [...prev, { device_serial: rejectionSerial }],
          );
        }
        return;
      }

      // Surfaced in the status page diagnostics rather than the console; the
      // raw event including correlation ids is loggable via ESP_DEBUG.
      if (ESP_DEBUG) {
        console.error(
          `proglove ${data.error_severity ?? 'ERROR'} ${data.error_code ?? ''}: ${data.error_message ?? ''}`,
          data,
        );
      }
      updateRecentErrors((prev) => [
        {
          eventId: data.event_id,
          time: data.time_created ?? Date.now(),
          severity: data.error_severity ?? ('ERROR' as const),
          code: data.error_code ?? null,
          message: data.error_message ?? null,
          deviceSerial: data.device_serial ?? null,
        },
        ...prev,
      ]);
    },
    [updateRecentErrors],
  );

  // Single socket-message handler for all ProGlove events: it parses once and
  // dispatches to the handlers above, which in turn fan out to the scan/button
  // listeners components register through this provider.
  const onSocketMessage = useStableCallback(
    (event: MessageEvent<string>) => {
      let data: ProGloveEvent;

      try {
        data = JSON.parse(event.data);
      } catch {
        console.warn('proglove websocket: ignoring malformed message', event.data);
        return;
      }

      if (typeof data?.event_type !== 'string') {
        console.warn('proglove websocket: ignoring message without event_type', data);
        return;
      }

      if (ESP_DEBUG) {
        console.log('proglove websocket received: ', data);
      }

      if (isProGloveEventError(data)) {
        onProGloveError(data);
        return;
      }

      if (isProGloveEventScan(data)) {
        onScan(data);
        return;
      }

      if (isProGloveEventButtonPress(data)) {
        onButtonPress(data);
        return;
      }

      if (isProGloveEventScannerState(data)) {
        onScannerStateChange(data);
        return;
      }

      if (isProGloveEventDisplayV2(data)) {
        onDisplayEvent(data);
        return;
      }

      console.warn('proglove websocket: ignoring unknown event type', data.event_type);
    },
    [onProGloveError, onScan, onButtonPress, onScannerStateChange, onDisplayEvent],
  );

  const onSocketOpen = useStableCallback(() => {
    // Ask Insight Mobile to initiate a scanner connection: unpaired it overlays
    // the SCAN2PAIR barcode, paired it rejects — which is how a reload
    // rediscovers the device (see README.md).
    pairScanner();
  }, [pairScanner]);

  useEffect(() => {
    addOnMessageListener(onSocketMessage);
    addOnOpenListener(onSocketOpen);

    connect();

    return () => {
      removeOnMessageListener(onSocketMessage);
      removeOnOpenListener(onSocketOpen);
      disconnect();
    };
    /** This hook has to run exactly once on-mount. All registered callbacks are stable. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addScanListener = useCallback((handler: ScanHandler) => {
    scanHandlersRef.current.add(handler);
  }, []);

  const addButtonPressListener = useCallback((handler: ButtonPressHandler) => {
    buttonPressHandlersRef.current.add(handler);
  }, []);

  const removeScanListener = useCallback((handler: ScanHandler) => {
    scanHandlersRef.current.delete(handler);
  }, []);

  const removeButtonPressListener = useCallback((handler: ButtonPressHandler) => {
    buttonPressHandlersRef.current.delete(handler);
  }, []);

  const addGloveActionListener = useCallback((handler: GloveActionHandler) => {
    gloveActionHandlersRef.current.add(handler);
  }, []);

  const removeGloveActionListener = useCallback((handler: GloveActionHandler) => {
    gloveActionHandlersRef.current.delete(handler);
  }, []);

  const phase: ProGlovePhase = !isWsConnected
    ? isWsConnecting
      ? ProGlovePhase.CONNECTING
      : ProGlovePhase.DISCONNECTED
    : scannerConnected
      ? ProGlovePhase.READY
      : ProGlovePhase.NO_SCANNER;

  return (
    <ProGloveContext.Provider
      value={{
        phase,
        devices,
        activeDeviceSerial: scannerDeviceSerial,
        lastScan,
        recentErrors,
        clearRecentErrors,
        pairScanner,
        showScreen,
        workerFeedback,
        buttonBlocking,
        addScanListener,
        addButtonPressListener,
        removeScanListener,
        removeButtonPressListener,
        addGloveActionListener,
        removeGloveActionListener,
      }}
    >
      {children}
    </ProGloveContext.Provider>
  );
}
